/**
 * @file mobile-ui-coordinator.ts
 * @description Handles mobile-specific UI interactions, panel toggling, and responsive logic.
 * #2074 implementation.
 */

import { EventBus } from '@hcie/shared';
import { ImageOps } from '../services/image-ops';

export function initMobileUICoordinator(): void {
    console.log("[Mobile-UI-Coordinator] Initializing mobile interaction layer...");

    // Expose toggle functions to window for onclick handlers in HTML
    (window as any).toggleMobileSidebar = toggleMobileSidebar;
    (window as any).showAdjustmentMenu = showAdjustmentMenu;
    (window as any).toggleAdvancedTools = toggleAdvancedTools;
    (window as any).openMobileColorPicker = openMobileColorPicker;
    (window as any).toggleMobilePanel = toggleMobilePanel;
    (window as any).closeAllMobilePanels = closeAllMobilePanels;
    (window as any).toggleMobileMenu = toggleMobileMenu;
    (window as any).toggleCategory = toggleCategory;
    (window as any).toggleMobileFilterDrawer = toggleMobileFilterDrawer;
    (window as any).applyQuickFilter = applyQuickFilter;

    const handleInitialMobileState = () => {
        if (window.innerWidth <= 768) {
            console.log("[Mobile-UI-Coordinator] Mobile viewport detected.");
            markEssentialTools(); // Ensure tools are visible
            closeAllMobilePanels();
            setupColorSync();
            renderQuickPalette();
            renderMobileMenu();
            injectToolChangeListener();
        }
    };
    
    window.addEventListener('resize', () => {
        if (window.innerWidth > 768) {
            // Cleanup mobile states if switching to desktop
            document.body.classList.remove('mobile-sidebar-active', 'mobile-panels-active');
        } else {
            setupColorSync();
        }
    });

    // Run once on init with a larger delay to ensure DOM and internationalization are ready
    setTimeout(handleInitialMobileState, 800);

    // Auto-close panels on color change (#2076-UX)
    EventBus.on('COLOR_CHANGED', () => {
        if (window.innerWidth <= 768) {
            closeAllMobilePanels();
        }
    });
}

/**
 * Closes all active mobile panels and sidebars.
 */
function closeAllMobilePanels(): void {
    document.body.classList.remove('mobile-sidebar-active', 'mobile-panels-active', 'menu-active');
    
    // Reset right sidebar specifics
    const container = document.getElementById('rightSidebarContainer');
    if (container) {
        container.classList.remove('swatches-active', 'properties-active', 'layers-active');
    }
    
    // Also close filter drawer
    const filterDrawer = document.getElementById('mobileFilterDrawer');
    if (filterDrawer) filterDrawer.classList.remove('active');

    document.querySelectorAll('.mobile-icon-btn').forEach(btn => {
        btn.classList.remove('active');
    });

    updateMobileNavState('none');
    console.log("[Mobile-UI] Closed all mobile panels.");
}

/**
 * Updates the active state of bottom navigation icons.
 */
function updateMobileNavState(activeId: 'tools' | 'filters' | 'layers' | 'docs' | 'none'): void {
    const navItems = document.querySelectorAll('.mobile-footer-item');
    navItems.forEach(item => item.classList.remove('active'));

    if (activeId !== 'none') {
        const activeItem = document.getElementById(`nav-${activeId}`);
        if (activeItem) activeItem.classList.add('active');
    }
}

/**
 * Renders a set of large color swatches for quick mobile selection.
 */
function renderQuickPalette(): void {
    const grid = document.getElementById('mobileQuickPalette');
    if (!grid) return;

    const colors = [
        '#000000', '#ffffff', '#808080', '#c0c0c0',
        '#ff0000', '#800000', '#00ff00', '#008000',
        '#0000ff', '#000080', '#ffff00', '#ffa500',
        '#800080', '#ffc0cb', '#00ffff', '#a52a2a'
    ];

    grid.innerHTML = '';
    colors.forEach(color => {
        const btn = document.createElement('div');
        btn.className = 'quick-color-btn';
        btn.style.backgroundColor = color;
        
        btn.addEventListener('click', () => {
            // Update global pen color via core state
            if ((window as any).g) {
                (window as any).g.pen_color = color;
                window.dispatchEvent(new CustomEvent('colorChanged', { detail: { color: color, source: 'mobile-quick' } }));
                EventBus.emit('COLOR_CHANGED', { color });
            }
            
            // Auto-close panels for immediate focus
            closeAllMobilePanels();
            
            // Visual feedback
            btn.style.transform = 'scale(0.9)';
            setTimeout(() => btn.style.transform = '', 100);
        });
        
        grid.appendChild(btn);
    });
}

/**
 * Syncs the mobile footer color indicator with the primary color preview.
 * Uses MutationObserver to detect background color changes.
 */
function setupColorSync(): void {
    const primaryPreview = document.getElementById('primary-color-preview');
    const mobileIndicator = document.getElementById('mobileColorIndicator');
    
    if (!primaryPreview || !mobileIndicator) return;

    // Initial sync
    mobileIndicator.style.backgroundColor = primaryPreview.style.backgroundColor;

    // Observe changes to the style attribute of the primary preview
    const observer = new MutationObserver(() => {
        mobileIndicator.style.backgroundColor = primaryPreview.style.backgroundColor;
    });

    observer.observe(primaryPreview, { attributes: true, attributeFilter: ['style'] });
}

/**
 * Triggers the primary color picker from mobile UI.
 */
function openMobileColorPicker(): void {
    // Open the swatches panel first
    toggleMobilePanel('swatches');
    
    // Slight delay to ensure DOM update then trigger picker
    setTimeout(() => {
        const primaryPreview = document.getElementById('primary-color-preview');
        if (primaryPreview) primaryPreview.click();
    }, 50);
}

/**
 * Toggles visibility of all tools in the toolbox on mobile.
 */
function toggleAdvancedTools(): void {
    const toolbox = document.getElementById('leftToolbox');
    if (toolbox) {
        toolbox.classList.toggle('show-all');
        const isShown = toolbox.classList.contains('show-all');
        
        // Handle all artistic/brush headers that should only appear in advanced mode
        document.querySelectorAll('.artistic-brushes-header').forEach(header => {
            (header as HTMLElement).style.display = isShown ? 'block' : 'none';
        });

        // Force a UI refresh for the toolbox icons
        toolbox.style.display = 'none';
        toolbox.offsetHeight; // force reflow
        toolbox.style.display = '';

        console.log(`[Mobile-UI] Toggled advanced tools. Mode: ${isShown ? 'Expert' : 'Essential'}`);
    }
}

/**
 * Toggles visibility of tools or properties panels on mobile.
 */
function toggleMobileSidebar(type: 'tools' | 'panels'): void {
    const className = type === 'tools' ? 'mobile-sidebar-active' : 'mobile-panels-active';
    const otherClass = type === 'tools' ? 'mobile-panels-active' : 'mobile-sidebar-active';
    
    // Toggle the target, and ensure the other is closed
    const isActive = document.body.classList.contains(className);
    
    // Close others
    document.body.classList.remove(otherClass, 'menu-active');
    const filterDrawer = document.getElementById('mobileFilterDrawer');
    if (filterDrawer) filterDrawer.classList.remove('active');
    
    if (isActive) {
        document.body.classList.remove(className);
        updateMobileNavState('none');
    } else {
        document.body.classList.add(className);
        updateMobileNavState(type === 'tools' ? 'tools' : 'layers'); // Defaulting panels to layers icon for now
    }
}

/**
 * Toggles a specific panel (swatches, properties, layers) on the mobile right sidebar.
 */
function toggleMobilePanel(panelId: 'swatches' | 'properties' | 'layers'): void {
    const container = document.getElementById('rightSidebarContainer');
    if (!container) return;

    const className = `${panelId}-active`;
    const isActive = container.classList.contains(className);

    // Close all desktop-sidebar related classes
    document.body.classList.remove('mobile-sidebar-active', 'menu-active');
    const filterDrawer = document.getElementById('mobileFilterDrawer');
    if (filterDrawer) filterDrawer.classList.remove('active');

    // Remove all active panel classes first to close others
    container.classList.remove('swatches-active', 'properties-active', 'layers-active');
    
    // Update button highlights
    document.querySelectorAll('.mobile-icon-btn').forEach(btn => {
        btn.classList.remove('active');
    });

    // If it wasn't active, open it now
    if (!isActive) {
        document.body.classList.add('mobile-panels-active');
        container.classList.add(className);
        updateMobileNavState('layers');
        
        // Highlight the specific button
        const btn = document.querySelector(`.mobile-icon-btn[onclick*="${panelId}"]`);
        if (btn) btn.classList.add('active');
        
        console.log(`[Mobile-UI-Coordinator] Opened panel: ${panelId}`);
    } else {
        document.body.classList.remove('mobile-panels-active');
        updateMobileNavState('none');
        console.log(`[Mobile-UI-Coordinator] Closed panel: ${panelId}`);
    }
}

/**
 * Specifically triggers the Adjustment/Filter menu on mobile.
 */
function showAdjustmentMenu(): void {
    // Open the mobile menu first
    toggleMobileMenu();
    
    // Focus on the 'Image' category which contains adjustments
    setTimeout(() => {
        const categories = document.querySelectorAll('.mobile-category');
        for (let cat of Array.from(categories)) {
            const title = cat.querySelector('.category-title span')?.textContent;
            if (title && (title.includes('Image') || title.includes('Görüntü') || title.includes('Adjustment'))) {
                (cat.querySelector('.category-title') as HTMLElement)?.click();
                break;
            }
        }
    }, 300);
}

/**
 * Toggles the main hamburger menu drawer.
 */
function toggleMobileMenu(): void {
    document.body.classList.toggle('menu-active');
    const isActive = document.body.classList.contains('menu-active');
    console.log(`[Mobile-UI-Coordinator] Hamburger menu toggled: ${isActive}`);
}

/**
 * Handles Category expansion in the mobile menu.
 */
function toggleCategory(event: Event): void {
    const title = event.currentTarget as HTMLElement;
    const parent = title.parentElement;
    if (parent) {
        const wasActive = parent.classList.contains('category-active');
        // Close all others
        document.querySelectorAll('.mobile-category').forEach(c => c.classList.remove('category-active'));
        if (!wasActive) {
            parent.classList.add('category-active');
        }
    }
}

/**
 * Dynamically builds the mobile menu Categories by scraping the desktop appMenuBar.
 */
function renderMobileMenu(): void {
    const container = document.getElementById('mobileMenuCategories');
    const desktopMenu = document.getElementById('appMenuBar');
    
    if (!container || !desktopMenu) return;
    container.innerHTML = '';
    
    const categories = desktopMenu.querySelectorAll('.menu-item');
    categories.forEach(cat => {
        const label = cat.querySelector('.menu-label')?.textContent || 'Untitled';
        const options = cat.querySelectorAll('.menu-option');
        
        if (options.length === 0) return;

        const catDiv = document.createElement('div');
        catDiv.className = 'mobile-category';
        
        const titleDiv = document.createElement('div');
        titleDiv.className = 'category-title';
        titleDiv.innerHTML = `<span>${label}</span> <span class="cat-arrow">⌄</span>`;
        titleDiv.onclick = (e) => toggleCategory(e);
        
        const optionsDiv = document.createElement('div');
        optionsDiv.className = 'category-options';
        
        options.forEach(opt => {
            const optLabel = opt.querySelector('span[data-i18n]')?.textContent || 
                             opt.querySelector('.menu-option-content')?.textContent?.trim() || 'Option';
            const originalOnclick = (opt as HTMLElement).onclick;
            
            const mobileOpt = document.createElement('div');
            mobileOpt.className = 'mobile-menu-option';
            mobileOpt.innerText = optLabel;
            
            mobileOpt.onclick = (e) => {
                if (originalOnclick) originalOnclick.call(opt as HTMLElement, e as any);
                toggleMobileMenu(); // Auto close menu after selection
            };
            
            optionsDiv.appendChild(mobileOpt);
        });
        
        catDiv.appendChild(titleDiv);
        catDiv.appendChild(optionsDiv);
        container.appendChild(catDiv);
    });

    // Add "Open Documents" category (#2081-Mobile-Docs)
    const g = (window as any).g;
    if (g && g.documents && g.documents.length > 0) {
        const docCatDiv = document.createElement('div');
        docCatDiv.className = 'mobile-category';
        
        const docTitleDiv = document.createElement('div');
        docTitleDiv.className = 'category-title';
        docTitleDiv.innerHTML = `<span>Open Documents</span> <span class="cat-arrow">⌄</span>`;
        docTitleDiv.onclick = (e) => toggleCategory(e);
        
        const docOptionsDiv = document.createElement('div');
        docOptionsDiv.className = 'category-options';
        
        g.documents.forEach((doc: any, index: number) => {
            const docOpt = document.createElement('div');
            docOpt.className = 'mobile-menu-option';
            if (index === g.activeDocumentIndex) docOpt.classList.add('active');
            docOpt.innerText = doc.name || `Image ${index + 1}`;
            
            docOpt.onclick = () => {
                if ((window as any).switchDocument) (window as any).switchDocument(index);
                toggleMobileMenu();
            };
            docOptionsDiv.appendChild(docOpt);
        });
        
        docCatDiv.appendChild(docTitleDiv);
        docCatDiv.appendChild(docOptionsDiv);
        container.appendChild(docCatDiv);
    }
    

    console.log("[Mobile-UI-Coordinator] Mobile menu rendered from desktop items and open documents.");
}

/**
 * Ensures essential tools have the .mobile-essential class to be visible by default in the drawer.
 */
function markEssentialTools(): void {
    const essentialTools = [
        'ToolPen', 'ToolBrush', 'ToolEraser', 'ToolEyeDropper', 
        'ToolFloodFill', 'ToolMagicWand', 'ToolText', 'ToolRectSelect', 
        'ToolMoveContent', 'ToolCrop', 'ToolRect', 'ToolLine', 'ToolShape'
    ];
    
    essentialTools.forEach(id => {
        const btn = document.getElementById(id);
        if (btn) btn.classList.add('mobile-essential');
    });
}

/**
 * Injects a wrapper around setTool to auto-close sidebars on mobile.
 */
function injectToolChangeListener(): void {
    const originalSetTool = (window as any).setTool;
    if (!originalSetTool || (window as any)._mobileSetToolInjected) return;

    (window as any).setTool = (tool: string) => {
        originalSetTool(tool);
        if (window.innerWidth <= 768) {
            closeAllMobilePanels();
        }
    };
    (window as any)._mobileSetToolInjected = true;
}

/**
 * Toggles the bottom filter presets drawer.
 */
function toggleMobileFilterDrawer(): void {
    const drawer = document.getElementById('mobileFilterDrawer');
    if (drawer) {
        drawer.classList.toggle('active');
        const isActive = drawer.classList.contains('active');
        
        // Close other drawers if open
        if (isActive) {
            document.body.classList.remove('menu-active', 'mobile-sidebar-active', 'mobile-panels-active');
            updateMobileNavState('filters');
        } else {
            updateMobileNavState('none');
        }
    }
}

/**
 * Applies a quick filter preset instantly.
 * #2076-Filters
 */
async function applyQuickFilter(type: string): Promise<void> {
    console.log(`[Mobile-UI] Applying quick filter: ${type}`);

    const gObj  = (window as any).g;
    const lIdx  = gObj?.activeLayerIndex ?? 0;
    const lArr  = (window as any).layers;
    const layer = lArr ? lArr[lIdx] : null;
    if (!layer?.ctx) return;

    const w        = layer.width  || gObj?.image_width  || layer.ctx.canvas.width;
    const h        = layer.height || gObj?.image_height || layer.ctx.canvas.height;
    const oldData  = layer.ctx.getImageData(0, 0, w, h);
    let   result: Uint8ClampedArray;

    switch (type) {
        case 'grayscale':
            result = await ImageOps.applyFilter(oldData.data, w, h, 'grayscale');
            break;
        case 'sepia':
            result = await ImageOps.applyFilter(oldData.data, w, h, 'sepia');
            break;
        case 'invert':
            result = await ImageOps.applyFilter(oldData.data, w, h, 'invert');
            break;
        case 'sharpen':
            result = await ImageOps.applyFilter(oldData.data, w, h, 'sharpen');
            break;
        case 'blur':
            result = await ImageOps.gaussianBlur(oldData.data, w, h, 2);
            break;
        case 'brightness':
            result = await ImageOps.colorAdjust(oldData.data, w, h, { brightness: 30 });
            break;
        case 'contrast':
            result = await ImageOps.colorAdjust(oldData.data, w, h, { contrast: 30 });
            break;
        default:
            return;
    }

    const newImageData = new ImageData(result, w, h);
    layer.ctx.putImageData(newImageData, 0, 0);

    // Push to history
    const hm = (window as any).historyManager;
    const DA = (window as any).DrawAction;
    if (hm && DA) hm.push(new DA(lIdx, oldData, newImageData));

    EventBus.emit('RENDER_LAYERS');
}
